import { normalizeWebsiteDomain } from '../cdmoApplicationRow';
import type { ExistingCdmoDomainMatch } from './validateRow';

/** Rows asked for per request. PostgREST's own cap (`max_rows`, 1000 on Supabase by default) may be lower. */
export const CDMO_WEBSITE_PAGE_SIZE = 1000;

/** An existing profile's website, as read for the bulk upload's duplicate check. */
export interface CdmoWebsiteRecord {
  id: string;
  company_name: string;
  website: string | null;
}

/**
 * Reads a query page by page until a page comes back empty. `from` and `to` are the inclusive row
 * offsets `.range()` takes. One request stops at the server's row cap without an error, so a list
 * read in one go silently loses its tail; paging on the rows actually received also copes with a
 * cap lower than `pageSize`.
 */
export async function fetchAllPages<T>(
  fetchPage: (from: number, to: number) => Promise<T[]>,
  pageSize = CDMO_WEBSITE_PAGE_SIZE
): Promise<T[]> {
  const all: T[] = [];
  for (let from = 0; ; ) {
    const page = await fetchPage(from, from + pageSize - 1);
    if (page.length === 0) return all;
    all.push(...page);
    from += page.length;
  }
}

/** One page of a query that also reports how many rows match in all (`null` when the server sends no count). */
export interface CountedPage<T> {
  rows: T[];
  total: number | null;
}

/**
 * {@link fetchAllPages} for a query that asks for the matching row count: once the rows read reach
 * that count, it stops without asking for the empty page that would otherwise end the loop. A short
 * result therefore costs one request. Without a count it reads until an empty page, as before.
 */
export async function fetchAllCounted<T>(
  fetchPage: (from: number, to: number) => Promise<CountedPage<T>>,
  pageSize = CDMO_WEBSITE_PAGE_SIZE
): Promise<T[]> {
  let total: number | null = null;
  return fetchAllPages(async (from, to) => {
    if (total !== null && from >= total) return [];
    const page = await fetchPage(from, to);
    total = page.total;
    return page.rows;
  }, pageSize);
}

/**
 * Longest `or` filter sent in one request. Each filter travels in the request URL, so a long list of
 * domains is split well below the URL length proxies accept.
 */
export const WEBSITE_FILTER_MAX_LENGTH = 2000;

/** Domains made only of these characters can go into a PostgREST filter as they are. */
const FILTER_SAFE_DOMAIN = /^[a-z0-9._-]+$/;

/**
 * Websites with a `%` or a character outside printable ASCII. The URL parser decodes their host before
 * it is compared (a `%` escape to its character, an international domain in its own script to its
 * punycode form), so a text search for the decoded domain cannot find them; they are always candidates.
 */
const ENCODED_WEBSITE_FILTER = String.raw`website.match.[^\x20-\x24\x26-\x7e]`;

/**
 * PostgREST `or` filters that together find every profile whose website could be on one of `domains`:
 * the website contains the domain, or is written encoded. They return a superset, which
 * {@link matchWebsiteDomains} narrows to exact matches. Several filters when the list is long.
 *
 * @returns The filters, or null when a domain has characters the filter syntax cannot carry; the
 * caller then reads every website
 */
export function websiteCandidateFilters(domains: readonly string[]): string[] | null {
  if (!domains.every((domain) => FILTER_SAFE_DOMAIN.test(domain))) return null;

  const filters: string[] = [];
  let parts: string[] = [];
  const close = () => {
    if (parts.length > 0) filters.push([...parts, ENCODED_WEBSITE_FILTER].join(','));
    parts = [];
  };
  for (const domain of domains) {
    const part = `website.ilike.*${domain}*`;
    const length = [...parts, part, ENCODED_WEBSITE_FILTER].join(',').length;
    if (length > WEBSITE_FILTER_MAX_LENGTH) close();
    parts.push(part);
  }
  close();
  return filters;
}

/**
 * Reads one page of the profiles that have a website, narrowed by an `or` filter from
 * {@link websiteCandidateFilters} (every such profile when the filter is null), with the matching count.
 */
export type WebsiteCandidatePageReader = (filter: string | null, from: number, to: number) => Promise<CountedPage<CdmoWebsiteRecord>>;

/**
 * The profiles whose website could be on one of `domains`: one search per filter, each read in full,
 * each profile listed once. Every website is read when a domain cannot go in a filter.
 *
 * @param domains - Domains as `normalizeWebsiteDomain` produces them
 * @param readPage - Reads one page of the search from the database
 * @returns A superset of the profiles on those domains; {@link matchWebsiteDomains} keeps the exact ones
 * @throws Whatever `readPage` throws, rather than returning a partial list
 */
export async function findWebsiteCandidates(
  domains: readonly string[],
  readPage: WebsiteCandidatePageReader
): Promise<CdmoWebsiteRecord[]> {
  const wanted = [...new Set(domains.filter(Boolean))];
  if (wanted.length === 0) return [];

  const filters = websiteCandidateFilters(wanted) ?? [null];
  const results = await Promise.all(filters.map((filter) => fetchAllCounted((from, to) => readPage(filter, from, to))));
  const candidates = new Map(results.flat().map((record) => [record.id, record]));
  return [...candidates.values()];
}

/**
 * The profiles whose current website has one of `domains`. The website is the source of truth: a
 * profile's stored `normalized_domain` can be empty (created before that column existed) or out of
 * date (website edited later), so it is never read.
 */
export function matchWebsiteDomains(
  records: readonly CdmoWebsiteRecord[],
  domains: readonly string[]
): ExistingCdmoDomainMatch[] {
  const wanted = new Set(domains);
  const matches: ExistingCdmoDomainMatch[] = [];
  for (const record of records) {
    const domain = normalizeWebsiteDomain(record.website ?? undefined);
    if (domain && wanted.has(domain)) {
      matches.push({ id: record.id, company_name: record.company_name, normalized_domain: domain });
    }
  }
  return matches;
}

/**
 * The duplicate lookup for one preview or one import. Every call asks the database again, and only
 * for the domains being checked, so a profile created moments ago is still found before a row is
 * inserted, and no call downloads every profile.
 *
 * @param findWebsiteCandidates - Profiles whose website could be on one of the domains (a superset)
 * @returns A lookup giving the profiles whose current website is on one of the domains
 */
export function createExistingDomainLookup(
  findWebsiteCandidates: (domains: string[]) => Promise<CdmoWebsiteRecord[]>
): (domains: string[]) => Promise<ExistingCdmoDomainMatch[]> {
  return async (domains) => {
    const wanted = [...new Set(domains.filter(Boolean))];
    if (wanted.length === 0) return [];
    return matchWebsiteDomains(await findWebsiteCandidates(wanted), wanted);
  };
}
